import { useState, Fragment, useEffect } from 'react';
import { DptCoverSheetProps, DyePenetrantInspectionProps, PDFImageProps, VTEquipmentAnomalyData } from '../../../../utils/models';
import { tutorialConstants } from '../../../../utils/constants';

import Button from '../../../themed/button';
import DPTReport from './';
import DptPhotographicRecordForm from '../../../forms/dpt/dptPhotographicRecord';
import DptCoverForm from '../../../forms/dpt/dptCoverForm';
import DyeInspectionItem from '../../../forms/dpt/dyeInspectionItem';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETAnomalySummary from '../et/etAnomalySummary';
import Modal from '../../../themed/modal';
import Zippy from '../../../themed/zippy';

import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateDPTReport = () => {
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [dptCoverData, setDptCoverData] = useState<DptCoverSheetProps>();
	const [photoRecords, setPhotoRecords] = useState<PDFImageProps[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [overview, setOverview] = useState<DyePenetrantInspectionProps>(details);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData,
			projectDetails,
			photoRecords: photoRecords,
			dyePenetrantInspection: overview,
			coverSheetData: dptCoverData,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setDptCoverData(cachedReport.coverSheetData);
			setOverview(cachedReport.dyePenetrantInspection);
			setPhotoRecords(cachedReport.photoRecords);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<DPTReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!dptCoverData}>
				<DptCoverForm onProceed={setDptCoverData} />
			</Zippy>

			<Zippy headerTitle="Dye Penetration Inspection" isValid={overview.data.length > 0}>
				<DyeInspectionItem onAdd={setOverview} />
			</Zippy>

			<Zippy
				headerTitle="Photographic Records"
				isValid={photoRecords.length > 0}
			>
				<DptPhotographicRecordForm onProceed={setPhotoRecords} />
			</Zippy>

			<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

const details: any = {
	data: [],
};

export default CreateDPTReport;
